import { ReportBlockTypes } from '@reports/domain/types';
import type { ReportChartType, ReportDataConfig } from '@reports/domain/types';

import type { BlockBase } from './BlockBase';
import { validateBlockBase } from './BlockBase';

/**
 * Configuration for a single chart series
 *
 * Maps data series to display labels and formatting rules.
 *
 * @property {string} key - Identifies the series in ChartBlockData.series array
 * @property {string} label - i18n key for the series label
 * @property {ReportDataConfig} format - Format configuration for y-axis values
 */
export type ChartSeriesConfig = Readonly<{
  key: string;
  label: string;
  format: ReportDataConfig;
}>;

/**
 * Value object representing a chart block in a report
 *
 * Encapsulates chart logic with validation and business rules.
 * Immutable by design to ensure data integrity.
 *
 * @example
 * const chartBlockConfig: ChartBlockConfig = createChartBlockConfig({
 *   key: 'sales_chart',
 *   title: 'Gráfico de ventas',
 *   description: 'Gráfico de ventas',
 *   endpoint: 'api/v1/sales/chart',
 *   type: ReportBlockTypes.CHART,
 *   chartType: ReportChartTypes.BAR,
 *   width: 'lg',
 *   seriesConfig: [
 *     {
 *       key: 'totalSales',
 *       label: 'reportSales:charts.totalSales.series.label',
 *       format: { type: ReportDataFormats.CURRENCY, options: { decimals: 2, prefix: '$' } }
 *     }
 *   ],
 *   options: {
 *     colors: ['#F04E4A'],
 *     showLegend: false,
 *     showLabels: true,
 *     showTooltip: true,
 *     groupTopValues: true,
 *   },
 * });
 */
export interface ChartBlockConfig extends BlockBase {
  readonly type: typeof ReportBlockTypes.CHART;
  readonly title?: string;
  readonly description?: string;
  readonly chartType: ReportChartType;
  readonly width?: 'sm' | 'md' | 'lg' | 'xl';
  readonly seriesConfig: ReadonlyArray<ChartSeriesConfig>;
  readonly options?: {
    readonly colors?: ReadonlyArray<string>;
    readonly showLegend?: boolean;
    readonly showLabels?: boolean;
    readonly showTooltip?: boolean;
    readonly showTopBarLabels?: boolean;
    readonly groupTopValues?: boolean;
  };
}

type CreateChartBlockConfigProps = {
  key: string;
  title?: string;
  description?: string;
  endpoint: string;
  type: typeof ReportBlockTypes.CHART;
  chartType: ReportChartType;
  width?: 'sm' | 'md' | 'lg' | 'xl';
  seriesConfig: ChartSeriesConfig[];
  options?: {
    colors?: string[];
    showLegend?: boolean;
    showLabels?: boolean;
    showTooltip?: boolean;
    showTopBarLabels?: boolean;
    groupTopValues?: boolean;
  };
};

/**
 * Factory function to create a chart block
 *
 * Validates that:
 * - seriesConfig has at least one entry
 * - Each series has key, label, and format
 *
 * @param props - The properties to create the chart block
 * @returns {ChartBlockConfig} The created chart block
 * @throws {Error} If validation fails
 */
export const createChartBlockConfig = (props: CreateChartBlockConfigProps): ChartBlockConfig => {
  validateBlockBase(props);

  // Validate seriesConfig
  if (!props.seriesConfig || props.seriesConfig.length === 0) {
    throw new Error('seriesConfig must have at least one entry');
  }

  // Validate each series configuration
  for (const series of props.seriesConfig) {
    if (!series.key) {
      throw new Error('Series key is required');
    }
    if (!series.label) {
      throw new Error('Series label is required');
    }
    if (!series.format || !series.format.type) {
      throw new Error('Series format is required');
    }
  }

  return Object.freeze({
    ...props,
    width: props.width ?? 'lg',
  });
};
